import React from 'react';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';
import {
  BaseCard,
  BaseCardHeader,
  HistogramChartCard,
  Icon,
  SkeletonWrapper,
} from '@neteera/neteera-web-nextgen';
import { Bar } from 'recharts';

import { displayInTimezone, FORMAT_WITHOUT_SECONDS } from 'src/utils/timeUtils';
import {
  StyledRow,
  ContentWrapper,
  StyledTimeFrame,
  StyledHr,
  LegendWrapper,
  LegendItem,
} from './styled';
import { PropsFromRedux } from './Connector';
import {
  buildHistogramChartData,
  DEFAULT_HISTOGRAM_CHART_PROPS,
} from './utils';
import { VS_METRICS } from 'src/utils/constants';

type Props = {
  intl: IntlShape;
  histogramData: PropsFromRedux['histogramData'];
  timeFrame: Omit<PropsFromRedux['timeFrame'], 'range'>;
  timezone: PropsFromRedux['timezone'];
  isHistogramDataLoading: boolean;
};

const HistogramComponent = ({
  histogramData,
  timeFrame,
  timezone,
  intl,
  isHistogramDataLoading,
}: Props): JSX.Element => {
  const startTimeWithTz = timeFrame.startTime
    ? displayInTimezone(timeFrame.startTime, timezone, FORMAT_WITHOUT_SECONDS)
    : '';
  const endTimeWithTz = timeFrame.endTime
    ? displayInTimezone(timeFrame.endTime, timezone, FORMAT_WITHOUT_SECONDS)
    : '';
  const hrHistogramData =
    histogramData?.HR && !isHistogramDataLoading
      ? buildHistogramChartData(histogramData, VS_METRICS.HR)
      : [];
  const rrHistogramData =
    histogramData?.RR && !isHistogramDataLoading
      ? buildHistogramChartData(histogramData, VS_METRICS.RR)
      : [];

  return (
    <BaseCard className="flex flex-col gap-10">
      <BaseCardHeader
        title={intl.formatMessage(messages.histogram)}
        leftIconProps={{ iconName: 'Chart' }}
        isLoading={isHistogramDataLoading}
        rightContentClassName="flex flex-1 items-center"
        size="base"
      >
        <StyledTimeFrame>{`${startTimeWithTz} - ${endTimeWithTz}`}</StyledTimeFrame>
      </BaseCardHeader>
      <SkeletonWrapper isLoading={isHistogramDataLoading} full>
        <ContentWrapper>
          <StyledRow>
            <HistogramChartCard
              title={intl.formatMessage(messages.heartRate)}
              iconProps={{
                iconName: 'Heart',
              }}
              histogramChartProps={{
                ...DEFAULT_HISTOGRAM_CHART_PROPS,
                data: hrHistogramData,
              }}
            >
              <Bar dataKey="ghost" barSize={0} />
            </HistogramChartCard>
            <HistogramChartCard
              title={intl.formatMessage(messages.respirationRate)}
              iconProps={{
                iconName: 'Lungs',
              }}
              histogramChartProps={{
                ...DEFAULT_HISTOGRAM_CHART_PROPS,
                data: rrHistogramData,
              }}
            >
              <Bar dataKey="ghost" barSize={0} />
            </HistogramChartCard>
          </StyledRow>
        </ContentWrapper>
        <div>
          <StyledHr />
          <LegendWrapper>
            <LegendItem>
              <Icon iconName="Chart" />
              <span>{intl.formatMessage(messages.current)}</span>
            </LegendItem>
            <LegendItem>
              <Icon iconName="Baseline" />
              <span>{intl.formatMessage(messages.last7days)}</span>
            </LegendItem>
          </LegendWrapper>
        </div>
      </SkeletonWrapper>
    </BaseCard>
  );
};

const messages = defineMessages({
  histogram: {
    id: 'HrRrHistogram.HistogramComponent.histogram',
    defaultMessage: 'Histogram',
  },
  heartRate: {
    id: 'HrRrHistogram.HistogramComponent.heartRate',
    defaultMessage: 'Heart Rate',
  },
  respirationRate: {
    id: 'HrRrHistogram.HistogramComponent.respirationRate',
    defaultMessage: 'Respiratory Rate',
  },
  current: {
    id: 'HrRrHistogram.HistogramComponent.current',
    defaultMessage: 'Selected time period',
  },
  last7days: {
    id: 'HrRrHistogram.HistogramComponent.last7days',
    defaultMessage: 'Previous 7 days to the Selected time period average',
  },
});

export default injectIntl(HistogramComponent);
